import { calculatePriority } from './priority';
import type { FoodContainer, IsoDate, QuantityUnit } from './model';

export interface RecipeStockIngredient {
  id: string;
  label: string;
  categoryIds: string[];
  nameTerms: string[];
  required: boolean;
  quantityPerPortion: number | null;
  unit: QuantityUnit | null;
}

export interface MealTemplate {
  id: string;
  title: string;
  description: string;
  acceptedCategoryIds: string[];
  requiredPantryIngredients: string[];
  optionalIngredients: string[];
  tags: string[];
  version: string;
  defaultPortions?: number;
  stockIngredients?: RecipeStockIngredient[];
}

export interface RecipeIngredientAvailability {
  ingredient: RecipeStockIngredient;
  containers: FoodContainer[];
  present: boolean;
  requiredQuantity: number | null;
  coveredQuantity: number | null;
  missingQuantity: number | null;
}

export interface MealDeductionProposal {
  ingredientId: string;
  ingredientLabel: string;
  container: FoodContainer;
  amount: number;
  unit: QuantityUnit;
}

export interface MealSuggestion {
  template: MealTemplate;
  portions: number;
  containers: FoodContainer[];
  ingredientAvailability: RecipeIngredientAvailability[];
  presentIngredients: string[];
  missingIngredients: string[];
  deductions: MealDeductionProposal[];
  score: number;
}

const normalize = (value: string): string =>
  value
    .normalize('NFD')
    .replaceAll(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('fr-FR')
    .trim();

const roundQuantity = (value: number): number =>
  Math.round((value + Number.EPSILON) * 1_000) / 1_000;

const convertQuantity = (
  value: number,
  from: QuantityUnit,
  to: QuantityUnit,
): number | null => {
  if (from === to) return value;
  if (from === 'kg' && to === 'g') return value * 1_000;
  if (from === 'g' && to === 'kg') return value / 1_000;
  if (from === 'l' && to === 'ml') return value * 1_000;
  if (from === 'ml' && to === 'l') return value / 1_000;
  return null;
};

const defaultStockIngredient = (template: MealTemplate): RecipeStockIngredient => ({
  id: `${template.id}-base`,
  label: 'Base disponible au froid',
  categoryIds: template.acceptedCategoryIds,
  nameTerms: [],
  required: true,
  quantityPerPortion: null,
  unit: null,
});

const matchesIngredient = (
  container: FoodContainer,
  ingredient: RecipeStockIngredient,
): boolean => {
  if (ingredient.categoryIds.includes(container.categoryId)) return true;
  const normalizedName = normalize(container.name);
  return ingredient.nameTerms.some((term) => normalizedName.includes(normalize(term)));
};

const priorityScore = (container: FoodContainer, today: IsoDate): number => {
  const level = calculatePriority(container, today).level;
  return level === 'overdue' || level === 'today'
    ? 5
    : level === 'urgent'
      ? 4
      : level === 'soon'
        ? 3
        : level === 'normal'
          ? 2
          : 1;
};

const buildDeductionPlan = (
  availability: RecipeIngredientAvailability[],
  portions: number,
): {
  availability: RecipeIngredientAvailability[];
  deductions: MealDeductionProposal[];
} => {
  const deductions: MealDeductionProposal[] = [];
  const remainingByContainer = new Map<string, number>();
  const quantified = availability.map((item): RecipeIngredientAvailability => {
    const { ingredient, containers } = item;
    if (ingredient.quantityPerPortion == null || ingredient.unit == null) return item;
    const requiredQuantity = roundQuantity(ingredient.quantityPerPortion * portions);
    let stillRequired = requiredQuantity;
    let hasConvertibleQuantity = false;
    for (const container of containers) {
      if (stillRequired <= 0 || container.remainingQuantity == null || container.unit == null) {
        continue;
      }
      const remainingInContainerUnit =
        remainingByContainer.get(container.id) ?? container.remainingQuantity;
      const available = convertQuantity(
        remainingInContainerUnit,
        container.unit,
        ingredient.unit,
      );
      if (available == null) continue;
      hasConvertibleQuantity = true;
      const usedInIngredientUnit = Math.min(stillRequired, available);
      const usedInContainerUnit = convertQuantity(
        usedInIngredientUnit,
        ingredient.unit,
        container.unit,
      );
      if (usedInContainerUnit == null || usedInContainerUnit <= 0) continue;
      const roundedUse = roundQuantity(usedInContainerUnit);
      deductions.push({
        ingredientId: ingredient.id,
        ingredientLabel: ingredient.label,
        container,
        amount: roundedUse,
        unit: container.unit,
      });
      remainingByContainer.set(
        container.id,
        roundQuantity(remainingInContainerUnit - roundedUse),
      );
      stillRequired = roundQuantity(stillRequired - usedInIngredientUnit);
    }
    return {
      ...item,
      requiredQuantity,
      coveredQuantity: hasConvertibleQuantity
        ? roundQuantity(requiredQuantity - stillRequired)
        : null,
      missingQuantity: hasConvertibleQuantity ? stillRequired : null,
    };
  });
  return { availability: quantified, deductions };
};

export const suggestMeal = (
  containers: FoodContainer[],
  template: MealTemplate,
  exclusions: string[],
  today: IsoDate,
  portions = template.defaultPortions ?? 2,
): MealSuggestion | null => {
  if (!Number.isInteger(portions) || portions < 1 || portions > 12) return null;
  const searchable = normalize(
    `${template.title} ${template.description} ${template.tags.join(' ')} ${template.requiredPantryIngredients.join(' ')}`,
  );
  const normalizedExclusions = exclusions.map(normalize).filter(Boolean);
  if (normalizedExclusions.some((excluded) => searchable.includes(excluded))) return null;

  const allowed = containers.filter(
    (container) =>
      ['present', 'partial'].includes(container.status) && container.incidentStatus !== 'unresolved',
  );
  const ingredients = template.stockIngredients ?? [defaultStockIngredient(template)];
  const rawAvailability = ingredients.map((ingredient): RecipeIngredientAvailability => {
    const matching = allowed
      .filter((container) => matchesIngredient(container, ingredient))
      .sort(
        (left, right) =>
          priorityScore(right, today) - priorityScore(left, today) ||
          left.updatedAt.localeCompare(right.updatedAt),
      );
    return {
      ingredient,
      containers: matching,
      present: matching.length > 0,
      requiredQuantity: null,
      coveredQuantity: null,
      missingQuantity: null,
    };
  });
  if (!rawAvailability.some((item) => item.present)) return null;

  const plan = buildDeductionPlan(rawAvailability, portions);
  const availability = plan.availability;

  const selectedContainers = [...new Map(
    availability.flatMap((item) => item.containers.slice(0, 3)).map((container) => [container.id, container]),
  ).values()];
  const missingStock = availability
    .filter((item) => item.ingredient.required)
    .flatMap((item) => {
      if (!item.present) return [item.ingredient.label];
      if (item.requiredQuantity != null && item.coveredQuantity == null) {
        return [`${item.ingredient.label} — quantité à vérifier`];
      }
      if (
        item.missingQuantity != null &&
        item.missingQuantity > 0 &&
        item.ingredient.unit != null
      ) {
        return [
          `${item.ingredient.label} — il manque ${item.missingQuantity} ${item.ingredient.unit}`,
        ];
      }
      return [];
    });
  const presentIngredients = availability
    .filter((item) => item.present)
    .map(
      (item) =>
        `${item.ingredient.label} — ${item.containers.slice(0, 2).map((container) => container.name).join(', ')}`,
    );
  const matchedScore = availability.reduce(
    (score, item) =>
      score +
      (item.present ? 5 : item.ingredient.required ? -3 : 0) +
      (item.containers[0] == null ? 0 : priorityScore(item.containers[0], today)),
    0,
  );
  return {
    template,
    portions,
    containers: selectedContainers,
    ingredientAvailability: availability,
    presentIngredients,
    missingIngredients: [...missingStock, ...template.requiredPantryIngredients],
    deductions: plan.deductions,
    score: matchedScore,
  };
};

export const suggestMeals = (
  containers: FoodContainer[],
  templates: MealTemplate[],
  exclusions: string[],
  today: IsoDate,
  limit = 3,
): MealSuggestion[] =>
  templates
    .map((template) => suggestMeal(containers, template, exclusions, today))
    .filter((suggestion): suggestion is MealSuggestion => suggestion != null)
    .sort(
      (left, right) =>
        right.score - left.score || left.template.title.localeCompare(right.template.title),
    )
    .slice(0, limit);
